import { useState, useEffect, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Upload, Loader2, ArrowLeft, ArrowRight } from "lucide-react";

const inputCls = "w-full px-3 py-2 border border-[var(--line)] outline-none text-sm rounded-md";
const lbl = "text-xs font-semibold text-[var(--ink-2)] mb-1 block";
const ICON_NAMES = ["FileText", "LineChart", "Globe", "MessageSquare", "GraduationCap", "Lightbulb", "PoundSterling", "Megaphone", "PlayCircle", "Filter", "Sparkles"];
const FIELD_TYPES = ["text", "textarea", "select", "date", "file"];

function ServiceDialog({ item, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const empty = { title: "", summary: "", image_url: "", video_url: "", icon: "Sparkles", order: 0, published: true, fields: [] };
  const [f, setF] = useState(item ? { ...empty, ...item } : empty);
  const [imgUp, setImgUp] = useState(false);
  const [vidUp, setVidUp] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const upImage = async (e) => {
    const file = e.target.files?.[0]; if (!file) return; setImgUp(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/media/image?folder=uploads/", fd, { headers: { "Content-Type": "multipart/form-data" } }); set("image_url", data.url); toast.success("Image uploaded"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"); } finally { setImgUp(false); e.target.value = ""; }
  };
  const upVideo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return; setVidUp(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/admin/content/upload-video", fd, { headers: { "Content-Type": "multipart/form-data" } }); set("video_url", data.video_url); toast.success("Video uploaded"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Video upload failed"); } finally { setVidUp(false); e.target.value = ""; }
  };

  const setField = (i, k, v) => setF((p) => { const fields = [...p.fields]; fields[i] = { ...fields[i], [k]: v }; return { ...p, fields }; });
  const addField = () => setF((p) => ({ ...p, fields: [...p.fields, { label: "", type: "text" }] }));
  const removeField = (i) => setF((p) => ({ ...p, fields: p.fields.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!f.title.trim()) { toast.error("Title is required"); return; }
    const payload = {
      ...f,
      fields: f.fields.filter((x) => x.label && x.label.trim()).map((x) => {
        const o = { label: x.label.trim(), type: x.type || "text" };
        if (x.type === "select" && x.optionsText) o.options = x.optionsText.split(",").map((s) => s.trim()).filter(Boolean);
        else if (x.options) o.options = x.options;
        if (x.desc) o.desc = true;
        if (x.web) o.web = true;
        if (x.opt) o.opt = true;
        return o;
      }),
    };
    setSaving(true);
    try {
      if (item) await api.put(`/admin/services/${item.id}`, payload);
      else await api.post("/admin/services", payload);
      toast.success(item ? "Saved" : "Service added"); setOpen(false); onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-xl max-w-lg">
        <DialogHeader><DialogTitle className="font-display">{item ? "Edit service" : "Add service"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className={lbl}>Title *</label><input data-testid="svc-title" value={f.title} onChange={(e) => set("title", e.target.value)} className={inputCls} /></div>
          <div><label className={lbl}>Summary</label><textarea data-testid="svc-summary" rows={3} value={f.summary} onChange={(e) => set("summary", e.target.value)} className={inputCls} /></div>
          <div>
            <label className={lbl}>Banner image (recommended 600×200)</label>
            {f.image_url && <img src={f.image_url} alt="" className="w-full aspect-[3/1] object-cover rounded-md mb-2 border border-[var(--line)]" />}
            <label data-testid="svc-image-upload" className="cursor-pointer inline-flex items-center gap-2 text-sm font-semibold border border-[var(--line)] px-4 py-2 rounded-md hover:bg-[var(--alt)]">
              {imgUp ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {f.image_url ? "Replace image" : "Upload image"}
              <input type="file" accept="image/*" className="hidden" disabled={imgUp} onChange={upImage} />
            </label>
          </div>
          <div>
            <label className={lbl}>Promo video (YouTube/Vimeo link or upload)</label>
            <input data-testid="svc-video" placeholder="https://youtube.com/watch?v=…" value={f.video_url} onChange={(e) => set("video_url", e.target.value)} className={inputCls} />
            <div className="flex items-center gap-2 mt-2">
              <label data-testid="svc-video-upload" className="cursor-pointer inline-flex items-center gap-2 text-sm font-semibold border border-[var(--line)] px-4 py-2 rounded-md hover:bg-[var(--alt)]">
                {vidUp ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload video
                <input type="file" accept="video/*" className="hidden" disabled={vidUp} onChange={upVideo} />
              </label>
              {f.video_url && <button type="button" onClick={() => set("video_url", "")} className="text-xs text-[#B71C1C] hover:underline">Remove</button>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Icon</label>
              <select data-testid="svc-icon" value={f.icon} onChange={(e) => set("icon", e.target.value)} className={inputCls}>{ICON_NAMES.map((n) => <option key={n}>{n}</option>)}</select></div>
            <div><label className={lbl}>Order</label><input data-testid="svc-order" type="number" value={f.order} onChange={(e) => set("order", parseInt(e.target.value || 0, 10))} className={inputCls} /></div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" data-testid="svc-published" checked={f.published} onChange={(e) => set("published", e.target.checked)} className="accent-[#1E6FEB]" /> Published (visible on the page)</label>

          <div className="border-t border-[var(--line)] pt-3">
            <div className="flex items-center justify-between mb-2"><span className="text-sm font-semibold">Enquiry form fields</span>
              <button type="button" onClick={addField} data-testid="svc-add-field" className="text-xs font-semibold text-[#1E6FEB] inline-flex items-center gap-1"><Plus size={13} /> Add field</button></div>
            <p className="text-xs text-[var(--ink-2)] mb-2">Name, business, email &amp; phone are always included. Add extra questions below. Tick "main description" on the field that should be the required "describe what you need" box.</p>
            {f.fields.map((fld, i) => (
              <div key={i} className="border border-[var(--line)] rounded-md p-2 mb-2 space-y-2" data-testid={`svc-field-${i}`}>
                <div className="flex gap-2">
                  <input placeholder="Question label" value={fld.label || ""} onChange={(e) => setField(i, "label", e.target.value)} className={`${inputCls} flex-1`} />
                  <select value={fld.type || "text"} onChange={(e) => setField(i, "type", e.target.value)} className={`${inputCls} w-28`}>{FIELD_TYPES.map((t) => <option key={t}>{t}</option>)}</select>
                  <button type="button" onClick={() => removeField(i)} className="text-[#B71C1C] px-2"><Trash2 size={15} /></button>
                </div>
                {fld.type === "select" && <input placeholder="Options, comma separated" value={fld.optionsText ?? (fld.options ? fld.options.join(", ") : "")} onChange={(e) => setField(i, "optionsText", e.target.value)} className={inputCls} />}
                <div className="flex gap-4 text-xs">
                  <label className="flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={!!fld.desc} onChange={(e) => setField(i, "desc", e.target.checked)} className="accent-[#1E6FEB]" /> Main description (required)</label>
                  <label className="flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={!!fld.web} onChange={(e) => setField(i, "web", e.target.checked)} className="accent-[#1E6FEB]" /> Website/link field</label>
                </div>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter><button onClick={save} disabled={saving} data-testid="svc-save" className="bg-[var(--accent)] text-white font-semibold px-5 py-2.5 rounded-md w-full disabled:opacity-60">{saving ? "Saving…" : "Save service"}</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ServicesManager() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/admin/services").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const remove = async (id) => { if (!window.confirm("Delete this service?")) return; await api.delete(`/admin/services/${id}`); toast.success("Deleted"); load(); };
  const move = async (i, dir) => {
    const t = i + dir; if (t < 0 || t >= items.length) return;
    const next = [...items]; [next[i], next[t]] = [next[t], next[i]]; setItems(next);
    try { await api.put("/admin/services-reorder", { ids: next.map((s) => s.id) }); } catch { load(); }
  };
  return (
    <div data-testid="services-manager">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[var(--ink-2)]">Services shown on the <strong>Business Development</strong> page. Add, edit, reorder, upload a banner image or promo video, and customise each enquiry form.</p>
        <ServiceDialog onSaved={load} trigger={<button data-testid="svc-new" className="inline-flex items-center gap-2 bg-[var(--accent)] text-white font-semibold px-4 py-2.5 rounded-md text-sm shrink-0"><Plus size={16} /> Add service</button>} />
      </div>
      <div className="space-y-2">
        {items.map((s, i) => (
          <div key={s.id} className="flex items-center gap-3 border border-[var(--line)] rounded-lg p-2.5 bg-white" data-testid={`service-row-${s.id}`}>
            <div className="w-24 aspect-[3/1] bg-[var(--alt)] rounded overflow-hidden shrink-0">{s.image_url && <img src={s.image_url} alt="" className="w-full h-full object-cover" />}</div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{s.title} {s.video_url && <span className="text-[10px] bg-[#1E6FEB] text-white px-1.5 py-0.5 rounded ml-1">VIDEO</span>}</div>
              <div className="text-xs text-[var(--ink-2)] truncate">{s.summary}</div>
            </div>
            <span className={`text-xs font-semibold shrink-0 ${s.published ? "text-[#2E7D32]" : "text-[var(--ink-2)]"}`}>{s.published ? "Visible" : "Hidden"}</span>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 disabled:opacity-30" title="Move up"><ArrowLeft size={14} className="rotate-90" /></button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-1 disabled:opacity-30" title="Move down"><ArrowRight size={14} className="rotate-90" /></button>
              <ServiceDialog item={s} onSaved={load} trigger={<button data-testid={`svc-edit-${s.id}`} className="text-[#1E6FEB] px-2"><Pencil size={15} /></button>} />
              <button onClick={() => remove(s.id)} data-testid={`svc-delete-${s.id}`} className="text-[#B71C1C] px-2"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="border border-[var(--line)] rounded-lg py-10 text-center text-[var(--ink-2)]">No services yet. Add your first one.</div>}
      </div>
    </div>
  );
}
